import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
// material
import { Box, Typography } from '@mui/material';
// hooks
import useAuth from '../../hooks/useAuth';
// components
import LightboxModal from '../../components/LightboxModal';
import InternalChatMessageItem from './InternalChatMessageItem';
// config
import { HOST_API } from '../../config';

// ----------------------------------------------------------------------

InternalChatMessageList.propTypes = {
    messages: PropTypes.array.isRequired,
    chat: PropTypes.object.isRequired,
    onReply: PropTypes.func,
    displayLimit: PropTypes.number,
    onLoadMore: PropTypes.func
};

export default function InternalChatMessageList({ messages, chat, onReply, displayLimit = 50, onLoadMore }) {
    const { user } = useAuth();
    const scrollRef = useRef(null);

    const [openLightbox, setOpenLightbox] = useState(false);
    const [selectedImage, setSelectedImage] = useState(0);

    const prevScrollHeightRef = useRef(0);
    const isFetchingOldRef = useRef(false);

    const handleLoadMore = useCallback(() => {
        if (scrollRef.current) {
            prevScrollHeightRef.current = scrollRef.current.scrollHeight;
            isFetchingOldRef.current = true;
        }
        if (onLoadMore) onLoadMore();
    }, [onLoadMore]);

    const displayedMessages = useMemo(() => messages?.slice(-displayLimit) || [], [messages, displayLimit]);
    const hasMoreMessages = (messages?.length || 0) > displayLimit;

    // Scroll to bottom on new messages or preserve anchor on load more
    useEffect(() => {
        if (scrollRef.current) {
            if (isFetchingOldRef.current) {
                // Restore scroll position after getting older messages
                const newScrollHeight = scrollRef.current.scrollHeight;
                scrollRef.current.scrollTop = newScrollHeight - prevScrollHeightRef.current;
                isFetchingOldRef.current = false;
            } else {
                // Auto-scroll to bottom on init or new messages
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }
        }
    }, [displayedMessages]);

    // Lightbox Logic
    const imagesLightbox = useMemo(() =>
        messages
            .filter(msg => msg.attachments?.some(att => att.type === 'image'))
            .flatMap(msg => msg.attachments
                .filter(att => att.type === 'image')
                .map(att => `${HOST_API}${att.url}?token=${window.localStorage.getItem('accessToken')}`)
            )
        , [messages]);

    const handleOpenLightbox = useCallback((url) => {
        const index = imagesLightbox.findIndex(img => img === url);
        setSelectedImage(index >= 0 ? index : 0);
        setOpenLightbox(true);
    }, [imagesLightbox]);

    return (
        <Box
            ref={scrollRef}
            sx={{
                flexGrow: 1,
                p: 3,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2
            }}
        >
            {hasMoreMessages && (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            cursor: 'pointer',
                            color: 'primary.main',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={handleLoadMore}
                    >
                        Carregar mais mensagens antigas
                    </Typography>
                </Box>
            )}

            {displayedMessages.map((msg) => {
                const senderId = (msg.senderId && typeof msg.senderId === 'object') ? msg.senderId._id : msg.senderId;
                const isMe = String(senderId) === String(user._id);

                return (
                    <Box key={msg._id} id={`msg-${msg._id}`}>
                        <InternalChatMessageItem
                            message={msg}
                            isMe={isMe}
                            participants={chat.participants}
                            currentUserId={user._id}
                            chatType={chat.type}
                            chatId={chat._id}
                            onOpenLightbox={handleOpenLightbox}
                            onReply={onReply}
                        />
                    </Box>
                );
            })}

            {openLightbox && (
                <LightboxModal
                    images={imagesLightbox}
                    mainSrc={imagesLightbox[selectedImage]}
                    photoIndex={selectedImage}
                    setPhotoIndex={setSelectedImage}
                    isOpen={openLightbox}
                    onCloseRequest={() => setOpenLightbox(false)}
                />
            )}
        </Box>
    );
}
